import { Link } from 'react-router-dom';

const trustItems = [
  { icon: 'ri-shield-check-line', label: 'Garantía 2 años' },
  { icon: 'ri-star-line', label: '+500 clientes' },
  { icon: 'ri-timer-line', label: '24/48h respuesta' },
  { icon: 'ri-map-pin-line', label: 'Málaga y alrededores' },
];

export default function Hero() {
  return (
    <section className="relative w-full min-h-[580px] md:min-h-[680px] lg:min-h-[780px] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="/images/010-hero-malaga-sun-2026.webp"
          alt="Terraza mediterránea con toldo en Málaga - Reparación e instalación de toldos"
          title="Toldos Málaga - Expertos en reparación e instalación de toldos"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
        />
      </div>

      {/* Gradient Overlay - center-focused for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/55"></div>

      {/* Center radial darkening for text contrast */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,oklch(0%_0_0_/_40%)_0%,oklch(0%_0_0_/_0%)_70%)]"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-8 pt-20 md:pt-24 pb-12 md:pb-16">
        <div className="flex flex-col items-center text-center gap-6 md:gap-8">
          {/* Status Badge */}
          <div
            className="animate-fade-in-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/20 shadow-lg shadow-black/20"
            style={{ animationDelay: '0.1s' }}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-accent-400 animate-pulse ring-2 ring-accent-400/40"></span>
            <span className="text-sm font-semibold text-white font-label tracking-wide whitespace-nowrap">
              Servicio urgente 24/48h en Málaga
            </span>
          </div>

          {/* H1 */}
          <h1
            className="animate-fade-in-up text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-extrabold text-white leading-[1.06] tracking-tight max-w-4xl drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]"
            style={{ animationDelay: '0.25s' }}
          >
            Tus toldos en las
            <br />
            mejores manos de{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent-400 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">Málaga</span>
              <span className="absolute bottom-1 left-0 right-0 h-2 md:h-3 bg-accent-500/30 rounded-full -z-0 blur-sm"></span>
            </span>
          </h1>

          {/* Description */}
          <p
            className="animate-fade-in-up text-base sm:text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed font-body drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]"
            style={{ animationDelay: '0.4s' }}
          >
            Reparación, instalación y mantenimiento de toldos. Tolderos locales con más de 15 años de experiencia. Presupuesto sin compromiso, respuesta rápida.
          </p>

          {/* CTA Buttons */}
          <div
            className="animate-fade-in-up flex flex-col sm:flex-row items-center gap-3 md:gap-4 pt-2"
            style={{ animationDelay: '0.55s' }}
          >
            <Link
              to="/presupuesto"
              className="group inline-flex items-center gap-2.5 px-7 sm:px-8 py-3.5 sm:py-4 bg-accent-500 text-foreground-950 text-sm sm:text-base md:text-lg font-bold rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap shadow-xl shadow-accent-500/25 hover:shadow-accent-400/40 hover:scale-105 active:scale-100"
            >
              <span>Presupuesto Gratis</span>
              <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
            <a
              href="tel:+34951120770"
              className="group inline-flex items-center gap-2.5 px-7 sm:px-8 py-3.5 sm:py-4 bg-white/10 backdrop-blur-md border-2 border-white/25 text-white text-sm sm:text-base md:text-lg font-bold rounded-full hover:bg-white/20 hover:border-white/40 transition-all duration-300 whitespace-nowrap hover:scale-105 active:scale-100"
            >
              <i className="ri-phone-line text-lg sm:text-xl text-accent-400 group-hover:scale-110 transition-transform duration-300"></i>
              <span>951 12 07 70</span>
            </a>
            <a
              href="https://wa.me/34627213347"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#25D366] text-white hover:bg-[#1fb855] transition-all duration-300 hover:scale-110 active:scale-100 shadow-lg shadow-[#25D366]/30"
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
            </a>
          </div>

          {/* Trust Badges */}
          <div
            className="animate-fade-in-up flex flex-wrap items-center justify-center gap-3 md:gap-4 pt-4 md:pt-6"
            style={{ animationDelay: '0.7s' }}
          >
            {trustItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/25 backdrop-blur-sm border border-white/20 shadow-sm shadow-black/15 hover:bg-black/30 transition-all duration-300"
              >
                <div className="w-7 h-7 rounded-full bg-accent-500/40 flex items-center justify-center">
                  <i className={`${item.icon} text-accent-400 text-xs`}></i>
                </div>
                <span className="text-xs md:text-sm font-medium text-white whitespace-nowrap font-label drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {/* Scroll Indicator */}
          <div
            className="animate-fade-in-up absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
            style={{ animationDelay: '1s' }}
          >
            <span className="text-[11px] text-white/50 font-label tracking-widest uppercase">
              Descubre más
            </span>
            <div className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center p-1">
              <div className="w-1 h-2 rounded-full bg-white/60 animate-scroll-bounce"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}